import { publishVisibleTasks } from "../../appControls/bridge"
import { taskSleepLabel } from "../../store/taskAttention"
import { pullRequestAutomationStatus } from "../../utils/pullRequestAutomation"
import { ContextMenu } from "@base-ui-components/react/context-menu"
import { useModal } from "@ebay/nice-modal-react"
import cx from "classnames"
import {
    CheckCircle,
    CheckSquare,
    ChevronDown,
    ChevronRight,
    Clock3,
    Copy,
    Diamond,
    Loader2,
    Mail,
    MailOpen,
    Monitor,
    Pencil,
    Pin,
    RotateCcw,
    Square,
    Trash2,
    X,
} from "lucide-react"
import { observer } from "mobx-react"
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { useOptionalComputers } from "../../computers/context"
import { type ProjectSource, type RemoteFleetTask, sourceLabel } from "../../computers/fleet"
import { usePortalContainer } from "../../hooks/usePortalContainer"
import { useCodeNavigate } from "../../routing"
import { useCodeStore } from "../../store/context"
import type { TaskCreation } from "../../store/managers/TaskCreationManager"
import { originalTaskDirectory } from "../../store/taskDirectory"
import type { CodeEvent } from "../../types"
import { copyTextToClipboard } from "../../utils/clipboard"
import { PersonGlyph } from "../computers/PersonGlyph"
import { shouldIgnoreMainInputContentEditableHotkey } from "../inputHotkeyScope"
import { TaskDeleteConfirm } from "./TaskDeleteConfirm"
import { GridDisclosure } from "./GridDisclosure"
import { ProjectIcon } from "./ProjectIcon"
import { queueModeManager } from "./queueModeManager"
import { codeSidebarManager } from "./sidebarManager"
import { type TaskCloseNavigationTarget, getNavigationTargetAfterTaskClose } from "./taskCloseNavigation"
import type { SidebarTaskDisplayEvent, SidebarTaskItem } from "./taskDisplayTypes"
import { partitionSidebarEntries } from "./taskListVisibility"
import { runtimeFirstTaskDisplayEvent } from "./taskRuntimeDisplay"
import { blendSidebarTaskQueues, sidebarTaskParked, type SidebarSortableTask } from "./taskSorting"
import { isSidebarTaskUnread } from "./taskUnread"

const TASK_NAVIGATION_HOTKEYS = "meta+alt+up,meta+alt+down"
type ClosedTaskView = "all" | "recent"
export interface SidebarTaskWorkspace {
    id: string
    name: string
    color?: string
    glyph?: string
    projectChars?: string
    projectEmoji?: string
}

/** Recent open tasks of a project on another computer, from its bounded overview. */
export interface SidebarRemoteQueue {
    workspace: SidebarTaskWorkspace
    nodeId: string
    source: ProjectSource
    tasks: RemoteFleetTask[]
}

type SidebarTaskEntry =
    | (SidebarSortableTask & {
          kind: "task"
          task: SidebarTaskItem
          workspace: SidebarTaskWorkspace
      })
    | (SidebarSortableTask & {
          kind: "creation"
          creation: TaskCreation
          workspace: SidebarTaskWorkspace
      })
    | (SidebarSortableTask & {
          kind: "remote"
          remote: RemoteFleetTask
          source: ProjectSource
          workspace: SidebarTaskWorkspace
      })

function remoteTaskToSidebarEntry(task: RemoteFleetTask, workspace: SidebarTaskWorkspace, source: ProjectSource): SidebarTaskEntry {
    return {
        kind: "remote",
        id: task.taskId,
        workspace,
        source,
        closed: false,
        createdAt: task.lastActivityAt,
        lastActivityAt: task.lastActivityAt,
        ...(task.running ? { lastEvent: { type: "action" as const, status: "in_progress" as const, sourceLabel: "Running", at: task.lastActivityAt } } : {}),
        attention: task.attention,
        sleeping: task.sleeping,
        remote: task,
    }
}

function taskToSidebarEntry(task: SidebarTaskItem, workspace: SidebarTaskWorkspace): SidebarTaskEntry {
    return {
        kind: "task",
        id: task.id,
        workspace,
        closed: task.closed,
        closedAt: task.closedAt,
        createdAt: task.createdAt,
        updatedAt: task.updatedAt,
        lastActivityAt: task.lastActivityAt,
        lastViewedAt: task.lastViewedAt,
        lastEvent: task.lastEvent,
        scheduledCount: task.scheduledCount,
        pullRequestAutomation: task.pullRequestAutomation,
        attention: task.attention,
        sleeping: task.sleeping,
        task,
    }
}

function taskCreationToSidebarEntry(creation: TaskCreation, workspace: SidebarTaskWorkspace): SidebarTaskEntry {
    return {
        kind: "creation",
        id: creation.id,
        workspace,
        closed: false,
        createdAt: creation.createdAt,
        lastEvent: {
            type: "action",
            status: creation.error ? "error" : "in_progress",
            sourceLabel: "Creating task",
            at: creation.createdAt,
        },
        creation,
    }
}

function codeEventToPreviewEvent(event: CodeEvent): SidebarTaskDisplayEvent {
    const sourceLabel = (() => {
        if (event.type === "action") return event.source.userLabel
        if (event.type === "setup_environment") return "Setup"
        return "Snapshot"
    })()

    return {
        type: event.type,
        status: event.status,
        sourceLabel,
        at: event.createdAt,
    }
}

function getInProgressEventForTask(codeStore: ReturnType<typeof useCodeStore>, item: SidebarTaskItem): SidebarTaskDisplayEvent | null {
    const taskId = item.id
    const task = codeStore.tasks.getTask(taskId)
    if (task && task.events.length > 0) {
        const lastEvent = task.events[task.events.length - 1]
        if (lastEvent.status === "in_progress") {
            return codeEventToPreviewEvent(lastEvent)
        }
    }

    return runtimeFirstTaskDisplayEvent(item.lastEvent, codeStore.isTaskRunning(taskId))
}

// ============================================================================
// Status helpers
// ============================================================================

function getStatusColor(lastEvent: SidebarTaskDisplayEvent): string {
    if (lastEvent.status === "error") return "text-error"
    return "text-success"
}

function getStatusIcon(lastEvent: SidebarTaskDisplayEvent, className = "w-3 h-3"): React.ReactNode {
    switch (lastEvent.status) {
        case "in_progress":
            return <Loader2 className={cx(className, "animate-spin")} />
        case "completed":
            return null
        case "error":
            return <X className={className} />
        case "stopped":
            return <Square className={className} />
    }
}

function statusLabel(lastEvent: SidebarTaskDisplayEvent): string | null {
    switch (lastEvent.status) {
        case "in_progress":
            return "in progress"
        case "completed":
            return null
        case "error":
            return "error"
        case "stopped":
            return "stopped"
    }
}

// ============================================================================
// Context menu item styling (matches Menu.tsx patterns)
// ============================================================================

const contextItemClassName =
    "flex cursor-pointer items-center gap-2 py-2 pr-3 pl-3 text-sm leading-4 outline-none select-none data-[highlighted]:relative data-[highlighted]:z-0 data-[highlighted]:text-white data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-0.5 data-[highlighted]:before:inset-y-0 data-[highlighted]:before:z-[-1] data-[highlighted]:before:bg-primary"

const contextPopupClassName =
    "origin-[var(--transform-origin)] bg-base-200 py-0.5 text-base-content shadow-lg outline outline-1 outline-border transition-[transform,scale,opacity] data-[ending-style]:scale-90 data-[ending-style]:opacity-0 data-[starting-style]:scale-90 data-[starting-style]:opacity-0 z-50"

const scheduledFollowUpLabel = (scheduledCount: number): string => (scheduledCount === 1 ? "1 scheduled follow-up" : `${scheduledCount} scheduled follow-ups`)

const WorkspaceCue = ({ workspace }: { workspace: SidebarTaskWorkspace }) => {
    return (
        <ProjectIcon
            projectId={workspace.id}
            projectName={workspace.name}
            color={workspace.color}
            glyph={workspace.glyph}
            projectChars={workspace.projectChars}
            projectEmoji={workspace.projectEmoji}
            variant="glyph"
            className="scale-70 opacity-75"
            showColorDot
        />
    )
}

const ScheduleCue = ({
    scheduledCount = 0,
    scheduledNeedsAttention = false,
    nextScheduledAt,
}: Pick<SidebarTaskItem, "scheduledCount" | "scheduledNeedsAttention" | "nextScheduledAt">) => {
    if (scheduledCount === 0) return null
    const label = scheduledFollowUpLabel(scheduledCount)
    const title = nextScheduledAt ? `${label} · Next: ${new Date(nextScheduledAt).toLocaleString()}` : label
    return (
        <span
            data-schedule-marker="true"
            className={cx("inline-flex flex-none items-center gap-0.5 text-[11px]", scheduledNeedsAttention ? "text-error" : "text-muted")}
            title={title}
            aria-label={label}
        >
            <Clock3 className="h-3 w-3" />
            {scheduledCount > 1 ? <span aria-hidden="true">{scheduledCount}</span> : null}
        </span>
    )
}

const StatusCue = ({ displayEvent }: { displayEvent?: SidebarTaskDisplayEvent | null }) => {
    if (!displayEvent || displayEvent.status === "completed") return null
    const label = statusLabel(displayEvent)
    return (
        <span
            data-status-marker={displayEvent.status}
            className={cx("inline-flex flex-none items-center", getStatusColor(displayEvent))}
            title={label ?? undefined}
            aria-label={label ?? undefined}
        >
            {getStatusIcon(displayEvent)}
        </span>
    )
}

const WorkspaceCueGroup = ({
    workspace,
    scheduledCount,
    scheduledNeedsAttention,
    nextScheduledAt,
    displayEvent,
}: {
    workspace: SidebarTaskWorkspace
    scheduledCount?: number
    scheduledNeedsAttention?: boolean
    nextScheduledAt?: string
    displayEvent?: SidebarTaskDisplayEvent | null
}) => (
    <span data-workspace-cue-group="true" className="inline-flex flex-none items-center gap-1">
        <StatusCue displayEvent={displayEvent} />
        <ScheduleCue scheduledCount={scheduledCount} scheduledNeedsAttention={scheduledNeedsAttention} nextScheduledAt={nextScheduledAt} />
        <WorkspaceCue workspace={workspace} />
    </span>
)

// ============================================================================
// TaskItem — single-line with right-click context menu
// ============================================================================

type TaskLifecycleMenuItem = { kind: "close"; label: "Close" } | { kind: "reopen"; label: "Reopen" }

export function taskLifecycleMenuItem(task: Pick<SidebarTaskItem, "closed">): TaskLifecycleMenuItem {
    if (!task.closed) return { kind: "close", label: "Close" }
    return { kind: "reopen", label: "Reopen" }
}

const TaskItem = ({
    task,
    workspace,
    isActive,
    isUnread,
    isPinned,
    isMajor,
    inProgressEvent,
    selectionMode,
    isSelected,
    onSelect,
    onToggleSelect,
    onEnterSelect,
    onDelete,
    onToggleClosed,
    onTogglePinned,
    onToggleMajor,
    onToggleRead,
    onCopyPath,
    onRename,
}: {
    task: SidebarTaskItem
    workspace?: SidebarTaskWorkspace
    isActive: boolean
    isUnread: boolean
    isPinned: boolean
    isMajor: boolean
    inProgressEvent: SidebarTaskDisplayEvent | null
    selectionMode: boolean
    isSelected: boolean
    onSelect: () => void
    onToggleSelect: () => void
    onEnterSelect: () => void
    onDelete: () => void
    onToggleClosed: () => void
    onTogglePinned: () => void
    onToggleMajor: () => void
    onToggleRead: () => void
    onCopyPath: () => void
    onRename: (newTitle: string) => void
}) => {
    const isClosed = task.closed ?? false
    const isParked = sidebarTaskParked(task)
    const lifecycleMenuItem = taskLifecycleMenuItem(task)
    const displayEvent = inProgressEvent ?? task.lastEvent
    const automationStatus =
        isParked && task.attention
            ? taskSleepLabel(task.attention)
            : task.pullRequestAutomation
              ? `${pullRequestAutomationStatus(task.pullRequestAutomation)}${task.pullRequestAutomation.number ? ` - #${task.pullRequestAutomation.number}` : ""}`
              : undefined
    const closedStatus = task.scheduledCount
        ? task.scheduledNeedsAttention
            ? "Closed · Scheduled work needs attention"
            : isUnread
              ? "Closed · Scheduled · Unread"
              : "Closed · Scheduled"
        : isUnread
          ? "Closed · Unread"
          : "Closed"
    const portalContainer = usePortalContainer()
    const [isEditing, setIsEditing] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    const handleCommit = () => {
        const value = inputRef.current?.value.trim()
        if (value && value !== task.title) {
            onRename(value)
        }
        setIsEditing(false)
    }

    const handleClick = selectionMode ? onToggleSelect : onSelect
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            handleClick()
        }
    }

    const rowContent = (
        <>
            {/* Selection checkbox OR pin indicator */}
            {selectionMode ? (
                <input
                    type="checkbox"
                    className="flex-shrink-0 accent-primary"
                    checked={isSelected}
                    onChange={onToggleSelect}
                    onClick={(e) => e.stopPropagation()}
                />
            ) : (
                isPinned && <Pin className="w-3 h-3 flex-shrink-0 text-primary" fill="currentColor" />
            )}

            {/* Title */}
            {isEditing ? (
                <input
                    ref={inputRef}
                    className="truncate min-w-0 flex-1 text-sm bg-transparent text-inherit border border-base-300 px-1 py-0 outline-none"
                    defaultValue={task.title}
                    autoFocus
                    onBlur={handleCommit}
                    onKeyDown={(e) => {
                        e.stopPropagation()
                        if (e.key === "Enter") handleCommit()
                        if (e.key === "Escape") setIsEditing(false)
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
            ) : (
                <span
                    className="truncate min-w-0 flex-1 select-none"
                    onDoubleClick={(e) => {
                        if (selectionMode) return
                        e.stopPropagation()
                        setIsEditing(true)
                    }}
                >
                    {task.title}
                </span>
            )}

            {isMajor ? (
                <span className="inline-flex flex-none text-primary" aria-label="Priority task" title="Priority task">
                    <Diamond className="h-3 w-3" fill="currentColor" />
                </span>
            ) : null}

            {workspace ? null : (
                <ScheduleCue
                    scheduledCount={task.scheduledCount}
                    scheduledNeedsAttention={task.scheduledNeedsAttention}
                    nextScheduledAt={task.nextScheduledAt}
                />
            )}

            {/* Status suffix */}
            {!isClosed && automationStatus && (
                <span className={cx("text-[11px] flex-shrink-0", task.pullRequestAutomation?.phase === "attention" ? "text-warning" : "text-muted")}>
                    {automationStatus}
                </span>
            )}
            {!isClosed && !isParked && displayEvent && (displayEvent.status !== "in_progress" || !workspace) && (
                <span className={cx("flex items-center gap-1 text-xs flex-shrink-0", getStatusColor(displayEvent))}>
                    {workspace ? null : getStatusIcon(displayEvent)}
                    {displayEvent.status !== "in_progress" && <span>{displayEvent.sourceLabel}</span>}
                </span>
            )}

            {isClosed && (
                <span className={cx("text-[11px] flex-shrink-0", task.scheduledNeedsAttention ? "text-error" : isUnread ? "text-info" : "text-muted")}>
                    {closedStatus}
                </span>
            )}

            {workspace ? (
                <WorkspaceCueGroup
                    workspace={workspace}
                    scheduledCount={task.scheduledCount}
                    scheduledNeedsAttention={task.scheduledNeedsAttention}
                    nextScheduledAt={task.nextScheduledAt}
                    displayEvent={!isClosed ? displayEvent : null}
                />
            ) : null}
        </>
    )

    const rowClassName = cx(
        "group btn flex min-h-9 w-full cursor-pointer items-center gap-2 border-l-2 py-1.5 pl-2 pr-2 text-[13px] font-normal transition-colors max-[900px]:min-h-11",
        isClosed || isParked ? "text-muted" : "text-base-content",
        isActive && !selectionMode ? "bg-base-300 hover:bg-base-300" : selectionMode && isSelected ? "bg-primary/10 hover:bg-primary/20" : "hover:bg-base-300",
        isUnread ? "border-l-info" : "border-l-transparent"
    )

    if (selectionMode) {
        return (
            <div role="button" tabIndex={0} className={rowClassName} onClick={handleClick} onKeyDown={handleKeyDown} title={task.title}>
                {rowContent}
            </div>
        )
    }

    return (
        <ContextMenu.Root>
            <ContextMenu.Trigger
                className="flex w-full"
                render={<div role="button" tabIndex={0} className={rowClassName} onClick={handleClick} onKeyDown={handleKeyDown} title={task.title} />}
            >
                {rowContent}
            </ContextMenu.Trigger>
            <ContextMenu.Portal container={portalContainer}>
                <ContextMenu.Positioner className="outline-none z-50" sideOffset={4}>
                    <ContextMenu.Popup className={contextPopupClassName}>
                        <ContextMenu.Item className={contextItemClassName} onClick={onTogglePinned}>
                            <Pin className="w-4 h-4" fill={isPinned ? "currentColor" : "none"} />
                            <span>{isPinned ? "Unpin" : "Pin"}</span>
                        </ContextMenu.Item>
                        <ContextMenu.Item className={contextItemClassName} onClick={onToggleMajor}>
                            <Diamond className="h-4 w-4" fill={isMajor ? "currentColor" : "none"} />
                            <span>{isMajor ? "Make Regular" : "Make Priority"}</span>
                        </ContextMenu.Item>
                        <ContextMenu.Item className={contextItemClassName} onClick={onToggleRead}>
                            {isUnread ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                            <span>{isUnread ? "Mark read" : "Mark unread"}</span>
                        </ContextMenu.Item>
                        <ContextMenu.Item className={contextItemClassName} onClick={onCopyPath}>
                            <Copy className="w-4 h-4" />
                            <span>Copy path</span>
                        </ContextMenu.Item>
                        <ContextMenu.Item className={contextItemClassName} onClick={() => setTimeout(() => setIsEditing(true), 0)}>
                            <Pencil className="w-4 h-4" />
                            <span>Rename</span>
                        </ContextMenu.Item>
                        <ContextMenu.Item className={contextItemClassName} onClick={onToggleClosed}>
                            {isClosed ? <RotateCcw className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                            <span>{lifecycleMenuItem.label}</span>
                        </ContextMenu.Item>
                        <ContextMenu.Item className={contextItemClassName} onClick={onEnterSelect}>
                            <CheckSquare className="w-4 h-4" />
                            <span>Select</span>
                        </ContextMenu.Item>
                        <ContextMenu.Item className={contextItemClassName} onClick={onDelete}>
                            <Trash2 className="w-4 h-4" />
                            <span>Delete</span>
                        </ContextMenu.Item>
                    </ContextMenu.Popup>
                </ContextMenu.Positioner>
            </ContextMenu.Portal>
        </ContextMenu.Root>
    )
}

// ============================================================================
// RemoteTaskItem — a task on another computer; opening it switches there
// ============================================================================

const OriginCue = ({ source }: { source: ProjectSource }) => {
    if (source.kind === "shared") return <PersonGlyph name={source.personName} glyph={source.glyph} color={source.color} size="sm" />
    if (source.kind === "own") return <Monitor className="h-3 w-3 flex-none text-muted" aria-label={`On ${source.computerName}`} />
    return null
}

const RemoteTaskItem = ({
    entry,
    workspace,
    onOpen,
}: {
    entry: Extract<SidebarTaskEntry, { kind: "remote" }>
    workspace?: SidebarTaskWorkspace
    onOpen: () => void
}) => {
    const { remote, source } = entry
    return (
        <div
            role="button"
            tabIndex={0}
            className={cx(
                "group btn flex min-h-9 w-full cursor-pointer items-center gap-2 border-l-2 py-1.5 pl-2 pr-2 text-[13px] font-normal text-base-content transition-colors hover:bg-base-300 max-[900px]:min-h-11",
                remote.unread ? "border-l-info" : "border-l-transparent"
            )}
            data-remote-task={remote.taskId}
            data-remote-node={remote.nodeId}
            title={`${remote.title}\n${sourceLabel(source)}`}
            onClick={onOpen}
            onKeyDown={(e) => {
                if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault()
                    onOpen()
                }
            }}
        >
            <span className="truncate min-w-0 flex-1 select-none">{remote.title}</span>
            <span data-workspace-cue-group="true" className="inline-flex flex-none items-center gap-1">
                {remote.running ? <StatusCue displayEvent={entry.lastEvent} /> : null}
                <OriginCue source={source} />
                {workspace ? <WorkspaceCue workspace={workspace} /> : null}
            </span>
        </div>
    )
}

// ============================================================================
// CreatingTaskItem
// ============================================================================

const CreationCancelButton = ({ onCancel }: { onCancel: () => void }) => {
    const isTouchDevice = window.matchMedia("(hover: none) and (pointer: coarse)").matches

    return (
        <div
            className={cx("flex ml-auto flex-shrink-0", !isTouchDevice && "opacity-0 group-hover:opacity-100 transition-opacity")}
            onClick={(e) => {
                e.stopPropagation()
                onCancel()
            }}
        >
            <div className="flex flex-shrink-0 p-1 px-0.5 cursor-pointer hover:text-error">
                <X className="w-4 h-4 text-muted" />
            </div>
        </div>
    )
}

const CreatingTaskItem = ({
    creation,
    workspace,
    isActive,
    onSelect,
    onCancel,
}: {
    creation: TaskCreation
    workspace?: SidebarTaskWorkspace
    isActive: boolean
    onSelect: () => void
    onCancel: () => void
}) => {
    const hasError = creation.error !== null
    const displayEvent: SidebarTaskDisplayEvent = {
        type: "action",
        status: hasError ? "error" : "in_progress",
        sourceLabel: "Creating task",
        at: creation.createdAt,
    }

    return (
        <div
            role="button"
            tabIndex={0}
            className={cx(
                "group btn flex min-h-9 w-full cursor-pointer items-center gap-2 py-1.5 pl-2 pr-2 font-normal transition-colors max-[900px]:min-h-11",
                isActive ? "bg-base-300 hover:bg-base-300" : "hover:bg-base-300",
                hasError ? "text-error" : "text-muted"
            )}
            onClick={onSelect}
            onKeyDown={(e) => {
                if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault()
                    onSelect()
                }
            }}
            title={creation.description}
        >
            {workspace ? null : <Loader2 className={cx("w-4 h-4 flex-shrink-0", !hasError && "animate-spin")} />}
            <span className="truncate min-w-0 flex-1 select-none text-xs italic">{hasError ? "Creation failed..." : "Creating task..."}</span>
            <CreationCancelButton onCancel={onCancel} />
            {workspace ? <WorkspaceCueGroup workspace={workspace} displayEvent={displayEvent} /> : null}
        </div>
    )
}

interface MergedTasksSidebarContentProps {
    workspaces: SidebarTaskWorkspace[]
    remoteQueues?: SidebarRemoteQueue[]
    activeWorkspaceId: string | undefined
    taskId: string | undefined
    creationId?: string
    enableKeyboardNavigation?: boolean
}

export async function closeTaskAndRecordQueue(taskId: string, closeTask: () => Promise<void>): Promise<void> {
    await closeTask()
    queueModeManager.recordHandledTask(taskId, "close")
}

export const MergedTasksSidebarContent = observer(
    ({ workspaces, remoteQueues = [], activeWorkspaceId, taskId, creationId, enableKeyboardNavigation = true }: MergedTasksSidebarContentProps) => {
        const codeStore = useCodeStore()
        const computers = useOptionalComputers()
        const navigate = useCodeNavigate()
        const deleteConfirmModal = useModal(TaskDeleteConfirm)

        // ── Selection mode ──
        const [selectionMode, setSelectionMode] = useState(false)
        const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
        const [showClosedTasks, setShowClosedTasks] = useState(false)
        const [closedTaskView, setClosedTaskView] = useState<ClosedTaskView>("recent")
        const [retainedTopClosedTaskId, setRetainedTopClosedTaskId] = useState<string>()

        const enterSelectionMode = useCallback((initialId: string) => {
            setSelectionMode(true)
            setSelectedIds(new Set([initialId]))
        }, [])

        const exitSelectionMode = useCallback(() => {
            setSelectionMode(false)
            setSelectedIds(new Set())
        }, [])

        const toggleSelection = useCallback((id: string) => {
            setSelectedIds((prev) => {
                const next = new Set(prev)
                if (next.has(id)) next.delete(id)
                else next.add(id)
                return next
            })
        }, [])

        const pinnedSet = new Set(codeStore.personalSettingsStore?.settings.current.pinnedTaskIds ?? [])
        const runningEntryIds = new Set(codeStore.runtimes.runningTaskIds)
        const entryQueues = workspaces.map((workspace) => {
            const tasks = codeStore.tasks.getSidebarTasksForRepo(workspace.id)
            const creations = codeStore.creation.getCreationsForRepo(workspace.id)
            for (const creation of creations) {
                if (!creation.error) runningEntryIds.add(creation.id)
            }

            return {
                workspaceId: workspace.id,
                entries: [
                    ...tasks.map((task) => taskToSidebarEntry(task, workspace)),
                    ...creations.map((creation) => taskCreationToSidebarEntry(creation, workspace)),
                ],
            }
        })
        const remoteEntryQueues = remoteQueues.map((queue) => {
            for (const task of queue.tasks) {
                if (task.running) runningEntryIds.add(task.taskId)
            }
            return {
                workspaceId: queue.workspace.id,
                entries: queue.tasks.map((task) => remoteTaskToSidebarEntry(task, queue.workspace, queue.source)),
            }
        })
        const sortedEntries = blendSidebarTaskQueues([...entryQueues, ...remoteEntryQueues], {
            pinnedTaskIds: pinnedSet,
            runningTaskIds: runningEntryIds,
        })
        const queueModeTasks = sortedEntries.flatMap((entry) => {
            if (entry.kind !== "task" || entry.closed || sidebarTaskParked(entry) || runningEntryIds.has(entry.id)) return []
            const isMajor = entry.task.queuePriority === "major"
            if (pinnedSet.has(entry.id) && !isMajor) return []
            return [{ taskId: entry.id, workspaceId: entry.workspace.id, isMajor }]
        })
        const sortedTaskEntries = sortedEntries.filter((entry): entry is Extract<SidebarTaskEntry, { kind: "task" }> => entry.kind === "task")
        const now = new Date()
        const { allClosedEntries, recentClosedEntries, topEntries } = partitionSidebarEntries(sortedEntries, now, {
            retainedTopClosedEntryId: taskId === retainedTopClosedTaskId ? retainedTopClosedTaskId : undefined,
        })
        const visibleClosedEntries = closedTaskView === "recent" ? recentClosedEntries : allClosedEntries
        const topTaskEntries = topEntries.filter((entry): entry is Extract<SidebarTaskEntry, { kind: "task" }> => entry.kind === "task")
        const visibleClosedTaskEntries = visibleClosedEntries.filter((entry): entry is Extract<SidebarTaskEntry, { kind: "task" }> => entry.kind === "task")
        const visibleTaskEntries = [...topTaskEntries, ...(showClosedTasks ? visibleClosedTaskEntries : [])]
        useLayoutEffect(() =>
            publishVisibleTasks(
                [...topEntries, ...(showClosedTasks ? visibleClosedEntries : [])].flatMap((entry) =>
                    entry.kind === "creation"
                        ? []
                        : [
                              {
                                  nodeId:
                                      entry.kind === "remote" && entry.source.kind !== "local" ? entry.source.nodeId : (computers?.state.activeNodeId ?? ""),
                                  workspaceId: entry.workspace.id,
                                  taskId: entry.id,
                                  title: entry.kind === "remote" ? entry.remote.title : entry.task.title,
                              },
                          ]
                )
            )
        )
        const selectedOpenIds = sortedTaskEntries.filter((entry) => selectedIds.has(entry.id) && !entry.closed).map((entry) => entry.id)
        const activeTaskEntry = taskId === undefined ? undefined : sortedTaskEntries.find((entry) => entry.id === taskId)
        const activeTaskIsClosed = taskId !== undefined && allClosedEntries.some((entry) => entry.id === taskId)
        const activeTaskIsRecent = taskId !== undefined && recentClosedEntries.some((entry) => entry.id === taskId)
        const activeTopClosedTaskId = taskId !== undefined && topTaskEntries.some((entry) => entry.id === taskId && entry.closed) ? taskId : undefined
        const showWorkspaceNames = workspaces.length + remoteEntryQueues.length > 1

        useLayoutEffect(() => {
            queueModeManager.replaceEligibleTasks(queueModeTasks)
        })

        useEffect(
            () => () => {
                queueModeManager.clearEligibleTasks()
            },
            []
        )

        useLayoutEffect(() => {
            setRetainedTopClosedTaskId((current) => {
                if (activeTopClosedTaskId) return activeTopClosedTaskId
                if (current && (current !== taskId || !activeTaskEntry?.closed)) return undefined
                return current
            })
        }, [activeTaskEntry?.closed, activeTopClosedTaskId, taskId])

        useEffect(() => {
            if (activeTaskIsClosed) setShowClosedTasks(true)
            if (activeTaskIsClosed && !activeTaskIsRecent) setClosedTaskView("all")
        }, [activeTaskIsClosed, activeTaskIsRecent])

        const navigateTaskByOffset = useCallback(
            (offset: 1 | -1) => {
                if (visibleTaskEntries.length === 0) return

                const currentIndex = taskId ? visibleTaskEntries.findIndex((entry) => entry.id === taskId) : -1
                const baseIndex = currentIndex >= 0 ? currentIndex : offset === 1 ? -1 : 0
                const nextIndex = (baseIndex + offset + visibleTaskEntries.length) % visibleTaskEntries.length
                const nextEntry = visibleTaskEntries[nextIndex]
                queueModeManager.observeTask(nextEntry.id, !nextEntry.closed && !runningEntryIds.has(nextEntry.id))
                navigate.go("CodeWorkspaceTask", { workspaceId: nextEntry.workspace.id, taskId: nextEntry.id })
            },
            [navigate, runningEntryIds, taskId, visibleTaskEntries]
        )

        useHotkeys(
            TASK_NAVIGATION_HOTKEYS,
            (event) => {
                const offset = event.key === "ArrowUp" ? -1 : event.key === "ArrowDown" ? 1 : null
                if (offset === null) return

                event.preventDefault()
                navigateTaskByOffset(offset)
            },
            {
                enabled: enableKeyboardNavigation,
                enableOnContentEditable: true,
                ignoreEventWhen: shouldIgnoreMainInputContentEditableHotkey,
                preventDefault: true,
            },
            [navigateTaskByOffset]
        )

        // ── Keyboard shortcuts for selection mode ──
        const handleBulkDeleteRef = useRef<() => void>(() => {})

        useEffect(() => {
            if (!selectionMode) return
            const handler = (e: KeyboardEvent) => {
                const target = e.target as HTMLElement
                if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) return
                if (e.key === "Escape") {
                    exitSelectionMode()
                }
                if (e.key === "Delete" || e.key === "Backspace") {
                    handleBulkDeleteRef.current()
                }
            }
            window.addEventListener("keydown", handler)
            return () => {
                window.removeEventListener("keydown", handler)
            }
        }, [selectionMode, exitSelectionMode])

        // ── Handlers ──
        const handleSelectTask = (workspaceId: string, selectedTaskId: string, canHandle: boolean) => {
            queueModeManager.observeTask(selectedTaskId, canHandle)
            navigate.go("CodeWorkspaceTask", { workspaceId, taskId: selectedTaskId })
            codeSidebarManager.closeSidebarForNavigation()
        }

        // A task on another computer: switch the runtime there, then land on it.
        // The sidebar keeps its fleet list across the switch, so only the main
        // pane changes.
        const handleOpenRemoteTask = async (entry: Extract<SidebarTaskEntry, { kind: "remote" }>) => {
            if (!computers) return
            if (!(await computers.selectScope(entry.remote.nodeId))) return
            navigate.go("CodeWorkspaceTask", { workspaceId: entry.workspace.id, taskId: entry.remote.taskId })
            codeSidebarManager.closeSidebarForNavigation()
        }

        const handleSelectCreation = (workspaceId: string, selectedCreationId: string) => {
            navigate.go("CodeWorkspaceTaskCreating", { workspaceId, creationId: selectedCreationId })
            codeSidebarManager.closeSidebarForNavigation()
        }

        const navigateToCloseTarget = useCallback(
            (target: TaskCloseNavigationTarget | null) => {
                if (!target) return
                if (target.type === "task") {
                    navigate.go("CodeWorkspaceTask", { workspaceId: target.workspaceId, taskId: target.taskId })
                    return
                }

                navigate.go("CodeWorkspaceTaskCreate", { workspaceId: target.workspaceId })
            },
            [navigate]
        )

        const showDeleteConfirm = async (ids: string[], onDone?: () => void) => {
            const inventory = await codeStore.tasks.getResourceInventory(ids)
            deleteConfirmModal.show({
                tasks: inventory,
                onConfirm: async (options) => {
                    await codeStore.tasks.deepRemoveTasks(ids, options)
                    onDone?.()
                    if (activeWorkspaceId && taskId && ids.includes(taskId)) {
                        navigate.go("CodeWorkspace", { workspaceId: activeWorkspaceId })
                    }
                },
            })
        }

        const handleDeleteTask = async (deletedTaskId: string) => {
            await showDeleteConfirm([deletedTaskId])
        }

        const handleBulkDelete = async () => {
            if (selectedIds.size === 0) return
            const ids = [...selectedIds]
            await showDeleteConfirm(ids, exitSelectionMode)
        }
        handleBulkDeleteRef.current = handleBulkDelete

        const handleBulkClose = async () => {
            if (selectedOpenIds.length === 0) return
            await Promise.all(
                selectedOpenIds.map(async (id) => {
                    await closeTaskAndRecordQueue(id, () => codeStore.tasks.closeTask(id))
                })
            )
            exitSelectionMode()
        }

        const handleCancelCreation = async (workspaceId: string, cancelledCreationId: string) => {
            await codeStore.creation.cancelCreation(cancelledCreationId)
            if (creationId === cancelledCreationId) {
                navigate.go("CodeWorkspaceTaskCreate", { workspaceId })
            }
        }

        const handleToggleClosed = async (entry: Extract<SidebarTaskEntry, { kind: "task" }>) => {
            const lifecycleMenuItem = taskLifecycleMenuItem(entry.task)
            const navigationTasks = sortedTaskEntries.map((taskEntry) => ({
                id: taskEntry.id,
                workspaceId: taskEntry.workspace.id,
                closed: taskEntry.closed,
            }))
            const navigationTarget =
                lifecycleMenuItem.kind === "close" && entry.id === taskId ? getNavigationTargetAfterTaskClose(navigationTasks, entry.id) : null
            if (lifecycleMenuItem.kind === "reopen") await codeStore.tasks.setTaskClosed(entry.id, false)
            else await closeTaskAndRecordQueue(entry.id, () => codeStore.tasks.closeTask(entry.id))
            navigateToCloseTarget(navigationTarget)
        }

        const handleTogglePinned = (toggledTaskId: string) => {
            codeStore.tasks.toggleTaskPinned(toggledTaskId)
        }

        const handleToggleMajor = (toggledTask: SidebarTaskItem) => {
            void codeStore.tasks
                .setTaskQueuePriority(toggledTask.id, toggledTask.queuePriority === "major" ? undefined : "major")
                .catch((error) => console.error("[TaskList] Failed to update task Q priority:", error))
        }

        const handleToggleRead = (toggledTask: SidebarTaskItem) => {
            if (isSidebarTaskUnread(toggledTask)) {
                void codeStore.tasks.markTaskViewed(toggledTask.id)
            } else {
                void codeStore.tasks.markTaskUnread(toggledTask.id)
            }
        }

        const handleRenameTask = (renamedTaskId: string, newTitle: string) => {
            codeStore.tasks.setTaskTitle(renamedTaskId, newTitle)
        }

        const handleCopyTaskPath = async (workspaceId: string, selectedTaskId: string) => {
            const repo = codeStore.repos.getRepo(workspaceId)
            if (!repo) return

            try {
                const model = codeStore.tasks.getTaskModel(selectedTaskId)
                await model?.refreshDirectoryView()
                await copyTextToClipboard(model?.getDirectory("working")?.path ?? originalTaskDirectory(repo).path)
            } catch (err) {
                console.error("[TaskList] Failed to copy task path:", err)
            }
        }

        const selectAll = () => setSelectedIds(new Set(visibleTaskEntries.map((entry) => entry.id)))

        const renderEntry = (entry: SidebarTaskEntry) =>
            entry.kind === "remote" ? (
                <RemoteTaskItem
                    key={`${entry.remote.nodeId}:${entry.workspace.id}:${entry.remote.taskId}`}
                    entry={entry}
                    workspace={showWorkspaceNames ? entry.workspace : undefined}
                    onOpen={() => void handleOpenRemoteTask(entry)}
                />
            ) : entry.kind === "creation" ? (
                <CreatingTaskItem
                    key={`${entry.workspace.id}:${entry.creation.id}`}
                    creation={entry.creation}
                    workspace={showWorkspaceNames ? entry.workspace : undefined}
                    isActive={creationId === entry.creation.id}
                    onSelect={() => handleSelectCreation(entry.workspace.id, entry.creation.id)}
                    onCancel={() => handleCancelCreation(entry.workspace.id, entry.creation.id)}
                />
            ) : (
                <TaskItem
                    key={`${entry.workspace.id}:${entry.task.id}`}
                    task={entry.task}
                    workspace={showWorkspaceNames ? entry.workspace : undefined}
                    isActive={taskId === entry.task.id}
                    isUnread={isSidebarTaskUnread(entry.task)}
                    isPinned={pinnedSet.has(entry.task.id)}
                    isMajor={entry.task.queuePriority === "major"}
                    inProgressEvent={getInProgressEventForTask(codeStore, entry.task)}
                    selectionMode={selectionMode}
                    isSelected={selectedIds.has(entry.task.id)}
                    onSelect={() => handleSelectTask(entry.workspace.id, entry.task.id, !entry.closed && !runningEntryIds.has(entry.id))}
                    onToggleSelect={() => toggleSelection(entry.task.id)}
                    onEnterSelect={() => enterSelectionMode(entry.task.id)}
                    onDelete={() => handleDeleteTask(entry.task.id)}
                    onToggleClosed={() => handleToggleClosed(entry)}
                    onTogglePinned={() => handleTogglePinned(entry.task.id)}
                    onToggleMajor={() => handleToggleMajor(entry.task)}
                    onToggleRead={() => handleToggleRead(entry.task)}
                    onCopyPath={() => {
                        void handleCopyTaskPath(entry.workspace.id, entry.task.id)
                    }}
                    onRename={(newTitle) => handleRenameTask(entry.task.id, newTitle)}
                />
            )

        const isEmpty = topEntries.length === 0
        return (
            <div className="relative flex flex-col pb-1">
                {/* Selection mode header bar */}
                {selectionMode && (
                    <div className="flex items-center justify-between px-3 py-1.5 mx-1.5 mb-1 bg-base-200 text-base-content text-xs">
                        <span className="font-medium">{selectedIds.size} selected</span>
                        <div className="flex items-center gap-1.5">
                            <button type="button" className="btn px-2 py-1 text-xs hover:bg-base-300 transition-colors" onClick={selectAll}>
                                All
                            </button>
                            <button
                                type="button"
                                className="btn flex items-center gap-1 px-2 py-1 text-xs hover:bg-base-300 transition-colors"
                                onClick={handleBulkClose}
                                disabled={selectedOpenIds.length === 0}
                            >
                                <CheckCircle className="w-3 h-3" />
                                Close
                            </button>
                            <button
                                type="button"
                                className="btn flex items-center gap-1 px-2 py-1 text-xs text-error hover:bg-error/10 transition-colors"
                                onClick={handleBulkDelete}
                                disabled={selectedIds.size === 0}
                            >
                                <Trash2 className="w-3 h-3" />
                                Delete
                            </button>
                            <button type="button" className="btn px-1 py-1 text-xs hover:bg-base-300 transition-colors" onClick={exitSelectionMode}>
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-0.5 px-0.5">
                    {isEmpty ? (
                        <div className="px-2 py-2 text-sm text-muted">
                            {workspaces.length + remoteEntryQueues.length === 0 ? "No projects shown" : "No open tasks"}
                        </div>
                    ) : (
                        topEntries.map(renderEntry)
                    )}
                    {allClosedEntries.length > 0 ? (
                        <button
                            type="button"
                            className="btn flex min-h-7 items-center gap-1 self-start px-2 text-left text-[11px] text-muted/70 transition-colors hover:text-muted max-[900px]:min-h-10"
                            onClick={() => setShowClosedTasks((current) => !current)}
                            aria-expanded={showClosedTasks}
                        >
                            {showClosedTasks ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                            <span>{allClosedEntries.length} closed</span>
                        </button>
                    ) : null}
                    <GridDisclosure
                        open={showClosedTasks}
                        renderContent={() => (
                            <div className="mt-1 flex flex-col gap-0.5 border-t border-border/60 pt-1">
                                <div role="tablist" aria-label="Closed task view" className="flex items-center border-b border-border/60 px-1">
                                    {(["recent", "all"] satisfies ClosedTaskView[]).map((view) => {
                                        const selected = closedTaskView === view
                                        const count = view === "recent" ? recentClosedEntries.length : allClosedEntries.length
                                        return (
                                            <button
                                                key={view}
                                                type="button"
                                                role="tab"
                                                data-view={view}
                                                aria-selected={selected}
                                                className={cx(
                                                    "btn min-h-7 border-b-2 px-2 text-[11px] transition-colors max-[900px]:min-h-10",
                                                    selected ? "border-primary text-base-content" : "border-transparent text-muted/70 hover:text-muted"
                                                )}
                                                onClick={() => setClosedTaskView(view)}
                                                title={
                                                    view === "recent" ? "Up to 10 tasks closed in the past 2 days, plus scheduled tasks" : "All closed tasks"
                                                }
                                            >
                                                {view === "recent" ? "Recent" : "All"} · {count}
                                            </button>
                                        )
                                    })}
                                </div>
                                <div
                                    role="tabpanel"
                                    aria-label={`${closedTaskView === "recent" ? "Recent" : "All"} closed tasks`}
                                    className="flex flex-col gap-0.5"
                                >
                                    {visibleClosedEntries.length > 0 ? (
                                        visibleClosedEntries.map(renderEntry)
                                    ) : (
                                        <div className="px-2 py-2 text-xs text-muted">No tasks closed in the past 2 days</div>
                                    )}
                                </div>
                            </div>
                        )}
                    />
                </div>
            </div>
        )
    }
)

interface TasksSidebarContentProps {
    workspaceId: string
    taskId: string | undefined
    creationId?: string
    enableKeyboardNavigation?: boolean
}

export const TasksSidebarContent = observer(({ workspaceId, taskId, creationId, enableKeyboardNavigation }: TasksSidebarContentProps) => (
    <MergedTasksSidebarContent
        workspaces={[{ id: workspaceId, name: "" }]}
        activeWorkspaceId={workspaceId}
        taskId={taskId}
        creationId={creationId}
        enableKeyboardNavigation={enableKeyboardNavigation}
    />
))
